export enum RealtimeState {
    AVAILABLE = 'AVAILABLE',
    UNAVAILABLE = 'UNAVAILABLE',
    OUT_OF_ORDER = 'OUT_OF_ORDER',
    FAULTED = 'FAULTED',
    UNKNOWN = 'UNKNOWN'
}

export interface Promotion {
    id: number
    percentageDiscount: number
    startDate: string | null
    endDate: string | null
}

export interface Network {
    id: number
    name: string
    slug: string
    stationsCount?: number | null
    averageRating?: number | null
    ratingCount?: number | null
    warningMessage?: string | null
    remoteChargeHelpdocUrl?: string | null
    promotion?: Promotion | null
}

export interface PhoneNumber {
    countryCode?: string | null
    number: string
}

export enum OwnerType {
    CHARGEMAP_USER = 'CHARGEMAP_USER',
    NETWORK = 'NETWORK',
    THIRD_PARTY = 'THIRD_PARTY'
}

export interface Owner {
    type: OwnerType
    name?: string | null
    phone?: PhoneNumber | null
    email?: string | null
    website?: string | null
}

export interface Coordinates {
    lat: string
    lon: string
}

export interface PoolStatistic {
    checkinsCount: number
    reportsCount: number
    commentsCount: number
    ratingsCount: number
    globalNoteAverage?: number | null
    materialNoteAverage?: number | null
    priceNoteAverage?: number | null
    locationNoteAverage?: number | null
    securityNoteAverage?: number | null
    creationDate: string
}

export enum AuthenticationMethod {
    RFID = 'RFID',
    PINPAD = 'PINPAD',
    PHONE_APP = 'PHONE_APP',
    PHONE_NFC = 'PHONE_NFC',
    PHONE_CALL = 'PHONE_CALL',
    PHONE_SMS = 'PHONE_SMS',
    CREDIT_CARD = 'CREDIT_CARD',
    MONEY = 'MONEY',
    PRE_PAID = 'PRE_PAID',
    KEY = 'KEY',
    OTHER = 'OTHER'
}

export interface Pass {
    id: number
    name: string
    icon?: string | null
}

export enum OverallState {
    AVAILABLE = 'AVAILABLE',
    WORKING = 'WORKING',
    BUSY = 'BUSY',
    OUT_OF_SERVICE = 'OUT_OF_SERVICE',
    UNKNOWN = 'UNKNOWN'
}

export interface Connector {
    id: number
    power: number
    voltage?: number | null
    intensity?: number | null
    isBookable: boolean
    isCompatible?: boolean | null
    type?: string | null
    currentType?: string | null
    overallState?: OverallState | null
    realtimeState: string
    evseId?: number | null
    remoteIdentifier?: string | null
}

export interface Station {
    id: number
    label: string
    isFree?: boolean | null
    administrativeState: string
    authenticationMethods: AuthenticationMethod[]
    highlightedPasses: Pass[]
    thirdPartyPasses: Pass[]
    connectors: Connector[]
}

export enum Day {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
    SATURDAY = 'SATURDAY',
    SUNDAY = 'SUNDAY'
}

export interface Schedule {
    day: Day
    openingTime: string
    closingTime: string
}

export enum AccessType {
    CLIENT_ONLY = 'CLIENT_ONLY',
    EMPLOYEES_ONLY = 'EMPLOYEES_ONLY',
    RESIDENTS_ONLY = 'RESIDENTS_ONLY',
    MEMBERS_ONLY = 'MEMBERS_ONLY',
    PUBLIC = 'PUBLIC',
    MANUFACTURER_ONLY = 'MANUFACTURER_ONLY',
    AUTH_PERS_ONLY = 'AUTH_PERS_ONLY',
    UNSPECIFIED = 'UNSPECIFIED'
}

export enum AmenityType {
    DRINKS = 'drinks',
    RESTORATION = 'restoration',
    LODGING = 'lodging',
    SHOP = 'shop',
    RESTROOM = 'restroom',
    INTERNET_ACCESS = 'internet_access',
    BABY_AREA = 'baby_area',
    PARK = 'park',
    TOURIST_ATTRACTION = 'tourist_attraction'
}

export enum LocationType {
    AIRPORT = 'AIRPORT',
    ASSOCIATION = 'ASSOCIATION',
    CAMPSITE = 'CAMPSITE',
    CAR_DEALERSHIP = 'CAR_DEALERSHIP',
    CHURCH = 'CHURCH',
    CINEMA = 'CINEMA',
    COMPANY = 'COMPANY',
    HIGHWAY = 'HIGHWAY',
    HOSPITAL = 'HOSPITAL',
    HOTEL = 'HOTEL',
    MUSEUM = 'MUSEUM',
    PARK = 'PARK',
    PARKING = 'PARKING',
    PERSON = 'PERSON',
    POINT = 'POINT',
    PUBLIC_ROADS = 'PUBLIC_ROADS',
    RESTAURANT = 'RESTAURANT',
    SCHOOL = 'SCHOOL',
    SERVICE_STATION = 'SERVICE_STATION',
    SHOP = 'SHOP',
    STATION = 'STATION',
    TOWN_HALL = 'TOWN_HALL',
    UNKNOWN = 'UNKNOWN'
}

export enum Speed {
    NORMAL = 'NORMAL',
    MIDACCELERATED = 'MIDACCELERATED',
    ACCELERATED = 'ACCELERATED',
    FAST = 'FAST'
}

export enum ObjectState {
    CREATING = 'CREATING',
    PUBLISHED = 'PUBLISHED',
    DELETED = 'DELETED'
}

export interface Pool {
    id: number
    slug: string
    name: string
    speed: Speed
    network?: Network | null
    owner?: Owner | null
    number?: string | null
    streetName?: string | null
    postalCode?: string | null
    city: string
    countryName: string
    countryCode: string
    location: LocationType
    coordinates: Coordinates
    alwaysOpen: boolean
    indoor: boolean
    rating: string | null
    ratingCount: number | null
    statistic?: PoolStatistic | null
    chargemapPassCompatible: boolean
    sustainableEnergy?: boolean | null
    parkingFree?: boolean | null
    description?: string | null
    access: AccessType
    photoIllustration?: string | null
    dateCreated: string
    dateUpdated: string
    state: ObjectState
    amenities: AmenityType[]
    stations: Station[]
    schedules: Schedule[]
    shouldCheckPrices?: boolean | null
    isFree?: boolean | null
    isTesla: boolean
}

export interface PoolAccessType {
    slug: string
    label: string | null
}

export interface PoolReservationOption {
    slug: string
    label: string | null
}

export interface PoolSchedule {
    day: number
    openingTime: string
    closingTime: string
}

export interface PoolAccess {
    isAlwaysOpen: boolean
    accessType: PoolAccessType
    isParkingFree: boolean | null
    reservationOption: PoolReservationOption | null
    schedules: PoolSchedule[] | null
}

export interface Photos {
    comment: string | null
    id: number
    poolId: number
    url: string
}

export interface PoolPicture {
    count: number
    photos: Photos[]
}

export interface ChargingConnectorSnake {
    count: number
    availableCount: number
    evseIds: number[]
    type: string
    connectorType: {
        id: number
        icon: string
    }
    powerMax: number
}

export enum PoolPanelState {
    Preview = 'preview',
    Prices = 'prices',
    Simulator = 'simulator',
    VehicleSelection = 'vehicle-selection',
    ConnectorSelection = 'connector-selection'
}
